import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "@/components/ui/button";
import { Menu, X, User, ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { NAV_LABELS } from "../constants/uiCopy";

const Header = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [isScrolled, setIsScrolled] = React.useState(false);

  React.useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const closeMobile = () => setMobileMenuOpen(false);

  return (
    <header
      className={`sticky top-0 z-50 backdrop-blur-xl backdrop-saturate-150 border-b border-border/40 transition-colors duration-200 ${
        isScrolled ? "bg-background" : "bg-background/90"
      }`}
      data-testid="header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
        <div className="flex items-center justify-between h-14 sm:h-16 md:h-[4.25rem]">
          <Link
            to="/"
            className="flex flex-col items-start shrink-0"
            data-testid="logo-link"
          >
            <h1
              className="text-base sm:text-lg md:text-xl font-bold tracking-tight leading-tight"
              style={{ fontFamily: "Manrope, sans-serif" }}
            >
              Metal Concrete
            </h1>
            <span className="text-[11px] sm:text-xs md:text-sm text-muted-foreground leading-tight">
              Global Real Estate Network
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center space-x-6">
            <Link
              to="/search"
              className="text-[13px] font-medium hover:text-primary transition-colors"
            >
              Buscar
            </Link>

            <DropdownMenu>
              <DropdownMenuTrigger className="text-[13px] font-medium hover:text-primary flex items-center gap-1 transition-colors">
                Explorar <ChevronDown className="w-4 h-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem
                  onClick={() => navigate("/developments-list")}
                >
                  Desarrollos
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => navigate("/brokers-directory")}
                >
                  Asesores Inmobiliarios
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => navigate("/inventario-brokers")}
                >
                  {NAV_LABELS.collaborationInventory}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => navigate("/property-requests")}
                >
                  Búsquedas Activas
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <Link
              to="/pricing"
              className="text-[13px] font-medium hover:text-primary transition-colors"
            >
              {NAV_LABELS.pricing}
            </Link>
          </nav>

          {/* Desktop auth */}
          <div className="hidden lg:flex items-center gap-3">
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger className="flex items-center gap-2 text-sm hover:text-primary transition-colors">
                  <User className="w-4 h-4" />
                  <span className="max-w-[150px] truncate">{user.name}</span>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => navigate("/dashboard")}>
                    Dashboard
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/account")}>
                    Mi Cuenta
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/account?tab=subscription")}>
                    Suscripción
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/property-requests")}>
                    Búsquedas Activas
                  </DropdownMenuItem>
                  {(user?.role === "basic" ||
                    user?.role === "owner" ||
                    user?.role === "broker" ||
                    user?.role === "developer") && (
                    <DropdownMenuItem onClick={() => navigate("/add-property")}>
                      Nueva Propiedad
                    </DropdownMenuItem>
                  )}
                  {user?.role === "developer" && (
                    <DropdownMenuItem onClick={() => navigate("/add-development")}>
                      Nuevo Desarrollo
                    </DropdownMenuItem>
                  )}
                  {user?.role === "admin" && (
                    <>
                      <DropdownMenuItem onClick={() => navigate("/admin/users")}>
                        Gestión de Usuarios
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate("/admin/brokers")}>
                        Gestión de Brokers
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate("/admin/settings")}>
                        Configuración Admin
                      </DropdownMenuItem>
                    </>
                  )}
                  {user?.role === "broker" && (
                    <DropdownMenuItem onClick={() => navigate("/broker-profile/edit")}>
                      Editar Perfil Broker
                    </DropdownMenuItem>
                  )}
                  {user?.role === "developer" && (
                    <DropdownMenuItem onClick={() => navigate("/developer-profile/edit")}>
                      Editar Perfil Desarrollador
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={() => navigate("/favorites")}>
                    Favoritos
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/inquiries")}>
                    Consultas
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout}>
                    Cerrar Sesión
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" size="sm" data-testid="login-button">
                    Iniciar Sesión
                  </Button>
                </Link>
                <Link to="/register">
                  <Button size="sm" data-testid="register-button">
                    Registrarse
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile hamburger */}
          <button
            className="lg:hidden p-2 hover:bg-muted rounded-md transition-colors"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-button"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div
            className="lg:hidden py-4 border-t border-border/40 animate-in slide-in-from-top-2 duration-200 max-h-[calc(100vh-5rem)] overflow-y-auto"
            data-testid="mobile-menu"
          >
            <nav className="flex flex-col space-y-1">
              <Link
                to="/search"
                className="text-sm font-medium py-2.5 px-3 rounded-md hover:bg-muted transition-colors"
                onClick={closeMobile}
              >
                Buscar
              </Link>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-3 pt-3 pb-1">
                Explorar
              </p>
              <Link
                to="/developments-list"
                className="text-sm py-2.5 px-3 pl-6 rounded-md hover:bg-muted transition-colors"
                onClick={closeMobile}
              >
                Desarrollos
              </Link>
              <Link
                to="/brokers-directory"
                className="text-sm py-2.5 px-3 pl-6 rounded-md hover:bg-muted transition-colors"
                onClick={closeMobile}
              >
                {NAV_LABELS.brokersDirectory}
              </Link>
              <Link
                to="/inventario-brokers"
                className="text-sm py-2.5 px-3 pl-6 rounded-md hover:bg-muted transition-colors"
                onClick={closeMobile}
              >
                {NAV_LABELS.collaborationInventory}
              </Link>
              <Link
                to="/property-requests"
                className="text-sm py-2.5 px-3 pl-6 rounded-md hover:bg-muted transition-colors"
                onClick={closeMobile}
              >
                Búsquedas Activas
              </Link>

              <Link
                to="/pricing"
                className="text-sm font-medium py-2.5 px-3 rounded-md hover:bg-muted transition-colors"
                onClick={closeMobile}
              >
                {NAV_LABELS.pricing}
              </Link>

              {user && (
                <>
                  <div className="border-t border-border/40 my-2" />
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-3 pt-1 pb-1">
                    Mi Cuenta
                  </p>
                  <Link
                    to="/dashboard"
                    className="text-sm py-2.5 px-3 pl-6 rounded-md hover:bg-muted transition-colors"
                    onClick={closeMobile}
                  >
                    Dashboard
                  </Link>
                  <Link
                    to="/favorites"
                    className="text-sm py-2.5 px-3 pl-6 rounded-md hover:bg-muted transition-colors"
                    onClick={closeMobile}
                  >
                    Favoritos
                  </Link>
                  <Link
                    to="/inquiries"
                    className="text-sm py-2.5 px-3 pl-6 rounded-md hover:bg-muted transition-colors"
                    onClick={closeMobile}
                  >
                    Consultas
                  </Link>
                  <Link
                    to="/account"
                    className="text-sm py-2.5 px-3 pl-6 rounded-md hover:bg-muted transition-colors"
                    onClick={closeMobile}
                  >
                    Mi Cuenta
                  </Link>
                  <Link
                    to="/account?tab=subscription"
                    className="text-sm py-2.5 px-3 pl-6 rounded-md hover:bg-muted transition-colors"
                    onClick={closeMobile}
                  >
                    Suscripción
                  </Link>
                  <Link
                    to="/property-requests"
                    className="text-sm py-2.5 px-3 pl-6 rounded-md hover:bg-muted transition-colors"
                    onClick={closeMobile}
                  >
                    Búsquedas Activas
                  </Link>
                  {(user?.role === "basic" ||
                    user?.role === "owner" ||
                    user?.role === "broker" ||
                    user?.role === "developer") && (
                    <Link
                      to="/add-property"
                      className="text-sm py-2.5 px-3 pl-6 rounded-md hover:bg-muted transition-colors"
                      onClick={closeMobile}
                    >
                      Nueva Propiedad
                    </Link>
                  )}
                  {user?.role === "developer" && (
                    <Link
                      to="/add-development"
                      className="text-sm py-2.5 px-3 pl-6 rounded-md hover:bg-muted transition-colors"
                      onClick={closeMobile}
                    >
                      Nuevo Desarrollo
                    </Link>
                  )}
                  {user?.role === "admin" && (
                    <>
                      <Link
                        to="/admin/users"
                        className="text-sm py-2.5 px-3 pl-6 rounded-md hover:bg-muted transition-colors"
                        onClick={closeMobile}
                      >
                        Gestión de Usuarios
                      </Link>
                      <Link
                        to="/admin/brokers"
                        className="text-sm py-2.5 px-3 pl-6 rounded-md hover:bg-muted transition-colors"
                        onClick={closeMobile}
                      >
                        Gestión de Brokers
                      </Link>
                      <Link
                        to="/admin/settings"
                        className="text-sm py-2.5 px-3 pl-6 rounded-md hover:bg-muted transition-colors"
                        onClick={closeMobile}
                      >
                        Configuración Admin
                      </Link>
                    </>
                  )}
                  {user?.role === "broker" && (
                    <Link
                      to="/broker-profile/edit"
                      className="text-sm py-2.5 px-3 pl-6 rounded-md hover:bg-muted transition-colors"
                      onClick={closeMobile}
                    >
                      Editar Perfil Broker
                    </Link>
                  )}
                  {user?.role === "developer" && (
                    <Link
                      to="/developer-profile/edit"
                      className="text-sm py-2.5 px-3 pl-6 rounded-md hover:bg-muted transition-colors"
                      onClick={closeMobile}
                    >
                      Editar Perfil Desarrollador
                    </Link>
                  )}
                  <div className="pt-2 px-3">
                    <Button
                      onClick={() => {
                        logout();
                        closeMobile();
                      }}
                      variant="outline"
                      size="sm"
                      className="w-full"
                      data-testid="mobile-logout-btn"
                    >
                      Cerrar Sesión
                    </Button>
                  </div>
                </>
              )}
              {!user && (
                <>
                  <div className="border-t border-border/40 my-2" />
                  <div className="flex flex-col gap-2 px-3 pt-2">
                    <Link to="/login" onClick={closeMobile}>
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full"
                        data-testid="mobile-login-btn"
                      >
                        Iniciar Sesión
                      </Button>
                    </Link>
                    <Link to="/register" onClick={closeMobile}>
                      <Button
                        size="sm"
                        className="w-full"
                        data-testid="mobile-register-btn"
                      >
                        Registrarse
                      </Button>
                    </Link>
                  </div>
                </>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
